/**
 * RFC-1577 analytics for the Director composer (`chat_*`) and the workspace
 * header / publish / Project Settings surfaces (`canvas_*`).
 *
 * Call sites stay one line each: this module owns id minting, the closed-value
 * mappings (skill source, error class, setting categories) and the
 * once-per-activation bookkeeping, so the components never build properties
 * themselves. Nothing here carries free text — skill refs are checked against
 * their opaque shapes and dropped when they do not match.
 */

import { useEffect, useRef } from 'react'
import { ApiRequestError } from '../api/client'
import type { DirectorSkillSource, DirectorSkillSummary } from '../api/directorSkills'
import type { CanvasPerformanceSettings } from '../components/canvas-v2/canvasPerformanceSettings'
import { useOptionalProjectId } from '../contexts/ProjectIdContext'
import { createNadDebug } from '../utils/debugLog'
import { countBucket } from './buckets'
import { track } from './events'
import type {
  ProjectHeaderControlClickedProps,
  ProjectSettingChangedProps,
  ProjectPublishResultProps,
  ProjectPromptSubmittedProps,
  DirectorSkillsPickerActionProps,
  ComposerUploadMenuViewedProps,
  HeaderControl,
  ProjectSettingKey,
  PublishAction,
  PublishErrorCategory,
  SettingValueCategory,
  SkillPickerAction,
  SkillPickerTab,
  SkillSource,
  SkillPickerSource,
  SkillLibraryAction,
  ChatSkillLibraryActionProps,
} from './generated/analyticsEvents'
import { currentWorkspaceViewMode } from './assetDownloadAnalytics'
import { projectTeamContext } from './teamContext'
import { referenceOriginProps } from './chatReferenceOrigin'
import { peekProjectVisitId } from './projectVisit'

const log = createNadDebug('dw:analytics-events:chat-header')

export function newAnalyticsId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID()
    }
  } catch {
    /* fall through */
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`
}

// ── Skills ──────────────────────────────────────────────────────────────

const SKILL_SOURCE: Record<DirectorSkillSource, SkillSource> = {
  builtin: 'built_in',
  published: 'installed',
  user: 'user_created',
}

const UUID = '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}'
// The three runtime ref shapes minted by director_skills_service._ref():
// builtin:<code slug>, user:<skill uuid>, published:<skill uuid>[@<version uuid>].
// A builtin slug is Dreamwrite-authored; user-authored names never appear.
const SKILL_REF_RE = new RegExp(`^(builtin:[a-z0-9][a-z0-9_-]{0,63}|user:${UUID}|published:${UUID}(@${UUID})?)$`)
const SKILL_VERSION_ID_RE = new RegExp(`^${UUID}$`)

/** The skill's runtime ref when it has an opaque shape, else undefined. */
export function analyticsSkillRef(ref: string | undefined | null): string | undefined {
  return ref && SKILL_REF_RE.test(ref) ? ref : undefined
}

/** The skill version id when it is a UUID, else undefined. */
export function analyticsSkillVersionId(versionId: string | undefined | null): string | undefined {
  return versionId && SKILL_VERSION_ID_RE.test(versionId) ? versionId : undefined
}

export function skillSourceOf(source: DirectorSkillSource): SkillSource {
  return SKILL_SOURCE[source]
}

export interface SkillPickerAnalytics {
  /**
   * A skill became the selected skill (call only when the selection changed).
   * `source` is where in the picker it was picked; `position` its 1-based
   * place in the list it was picked from, when known.
   */
  selected(skill: DirectorSkillSummary, source: SkillPickerSource, position?: number): void
  /** The selected skill was removed by an action in the picker. */
  cleared(skill: DirectorSkillSummary): void
  /** The user opened a skill's detail view from the picker (analytics doc T2 #5). */
  detailViewed(skill: DirectorSkillSummary): void
}

/**
 * `director_skills_picker_action` for one SkillPicker. `visible` is true while the
 * compact panel OR the full gallery is showing: moving from one to the other
 * ("All") stays inside the same open. `tabSkillCount` is the number of skills
 * on the current tab, or undefined until a catalog load has completed.
 */
export function useSkillPickerAnalytics({
  visible,
  tab,
  tabSkillCount,
  installedSkillCount,
}: {
  visible: boolean
  tab: SkillPickerTab
  tabSkillCount: number | undefined
  /** Published skills the user has installed, or undefined until the catalog loaded. */
  installedSkillCount?: number
}): SkillPickerAnalytics {
  const projectId = useOptionalProjectId()
  const sessionRef = useRef<{ id: string; lastTab: SkillPickerTab | null } | null>(null)
  const latest = useRef({ projectId, tab, tabSkillCount, installedSkillCount })
  latest.current = { projectId, tab, tabSkillCount, installedSkillCount }

  const emitRef = useRef((action: SkillPickerAction, skill?: DirectorSkillSummary, extra?: { source?: SkillPickerSource; position?: number }) => {
    const session = sessionRef.current
    const { projectId: pid, tab: currentTab, tabSkillCount: count, installedSkillCount: installed } = latest.current
    // Outside a project (the landing composer) there is nothing to attribute to.
    if (!pid || !session) return
    const props: DirectorSkillsPickerActionProps = {
      project_id: pid,
      action,
      picker_open_id: session.id,
      tab: currentTab,
      skill_ref: skill ? analyticsSkillRef(skill.ref) : undefined,
      skill_version_id: skill ? analyticsSkillVersionId(skill.versionId) : undefined,
      skill_source: skill ? skillSourceOf(skill.source) : undefined,
      available_skill_count_bucket: count === undefined ? undefined : countBucket(count),
      // The compact picker opens only from the composer's skill button.
      entry_point: 'composer_button',
      installed_skill_count_bucket: installed === undefined ? undefined : countBucket(installed),
      source: extra?.source,
      selection_position_bucket: extra?.position === undefined ? undefined : countBucket(extra.position),
    }
    // A ref and its version travel together or not at all (co_present).
    if (!props.skill_ref || !props.skill_version_id) {
      props.skill_ref = undefined
      props.skill_version_id = undefined
    }
    log({ event: 'skills-picker-action', ...props })
    track('director_skills_picker_action', props)
  })

  useEffect(() => {
    if (!visible) {
      sessionRef.current = null
      return
    }
    if (!sessionRef.current) {
      // The tab the picker opens on is not a user action (no impressions in
      // the first release): `open` carries it in `tab`, and `tab_view` fires
      // only when the user switches tab afterwards.
      sessionRef.current = { id: newAnalyticsId(), lastTab: tab }
      emitRef.current('open')
      return
    }
    if (sessionRef.current.lastTab !== tab) {
      sessionRef.current.lastTab = tab
      emitRef.current('tab_view')
    }
  }, [visible, tab])

  const api = useRef<SkillPickerAnalytics>({
    selected: (skill, source, position) => emitRef.current('select', skill, { source, position }),
    cleared: (skill) => emitRef.current('clear', skill),
    detailViewed: (skill) => emitRef.current('detail_view', skill),
  })
  return api.current
}

// ── Composer ────────────────────────────────────────────────────────────

/** `project_prompt_submitted`: a Director turn the API accepted. */
export function trackChatPromptSubmitted({
  projectId,
  canvasId,
  selectedSkill,
  referencedNodeIds,
  attachmentCount,
}: {
  projectId: string
  canvasId?: string
  selectedSkill: { ref: string; version_id: string } | undefined
  referencedNodeIds: readonly string[] | undefined
  attachmentCount: number
}): void {
  const skillRef = analyticsSkillRef(selectedSkill?.ref)
  const skillVersionId = analyticsSkillVersionId(selectedSkill?.version_id)
  const both = skillRef !== undefined && skillVersionId !== undefined
  const props: ProjectPromptSubmittedProps = {
    project_id: projectId,
    has_skill: selectedSkill !== undefined,
    has_mentions: (referencedNodeIds?.length ?? 0) > 0,
    attachment_count: Math.max(0, Math.min(1000, Math.trunc(attachmentCount))),
    skill_ref: both ? skillRef : undefined,
    skill_version_id: both ? skillVersionId : undefined,
    // The server sends prompt_source='auto' for turns it starts on its own.
    prompt_source: 'composer',
    // The project composer lives in the chat sidebar only.
    entry_point: 'chat_sidebar',
    canvas_id: canvasId || undefined,
    project_visit_id: peekProjectVisitId(projectId) ?? undefined,
    view_mode: currentWorkspaceViewMode(),
    ...referenceOriginProps(referencedNodeIds),
  }
  log({ event: 'prompt-submitted', ...props })
  track('project_prompt_submitted', props)
}

/** `composer_upload_menu_viewed`: the composer's upload flyout opened. */
export function trackChatUploadMenuViewed(projectId: string | undefined, optionCount: number): void {
  if (!projectId) return
  const props: ComposerUploadMenuViewedProps = {
    project_id: projectId,
    entry_point: 'chat_sidebar',
    item_count: optionCount,
  }
  log({ event: 'upload-menu-viewed', ...props })
  track('composer_upload_menu_viewed', props)
}

// ── Header ──────────────────────────────────────────────────────────────

/** `project_header_control_clicked`: a header click that opens its surface. */
export function trackHeaderControlClicked(
  projectId: string,
  control: HeaderControl,
  editable: boolean | undefined,
): void {
  if (!projectId) return
  const props: ProjectHeaderControlClickedProps = {
    project_id: projectId,
    interaction_id: newAnalyticsId(),
    control,
    entry_point: 'project_header',
    editable,
    view_mode: currentWorkspaceViewMode(),
    owner_type: projectTeamContext(projectId).owner_type,
  }
  log({ event: 'header-control-clicked', ...props })
  track('project_header_control_clicked', props)
}

// ── Publish ─────────────────────────────────────────────────────────────

/** Coarse class of a failed publication request. Never the message. */
export function publishErrorCategory(err: Error | null): PublishErrorCategory {
  if (err instanceof ApiRequestError) {
    const status = err.status
    if (status === 401) return 'unauthorized'
    if (status === 403) return 'forbidden'
    if (status === 404) return 'not_found'
    if (status === 409) return 'conflict'
    if (status === 429) return 'rate_limited'
    if (status >= 500) return 'server_error'
    if (status >= 400) return 'client_error'
    return 'unknown'
  }
  // fetch() rejects with a TypeError when the request never got a response.
  if (err instanceof TypeError) return 'network'
  return 'unknown'
}

/** `project_publish_result`: one row per settled publish-popover action. */
export function trackPublishResult({
  projectId,
  action,
  error,
  versionCount,
}: {
  projectId: string
  action: PublishAction
  /** Undefined on success; the thrown value (or null when it is not an Error) on failure. */
  error?: Error | null
  versionCount: number | undefined
}): void {
  const failed = error !== undefined
  const props: ProjectPublishResultProps = {
    project_id: projectId,
    action,
    result: failed ? 'failure' : 'success',
    error_category: failed ? publishErrorCategory(error) : undefined,
    version_count_bucket: versionCount === undefined ? undefined : countBucket(versionCount),
  }
  log({ event: 'publish-result', ...props })
  track('project_publish_result', props)
}

// ── Project Settings ────────────────────────────────────────────────────

export const CANVAS_PERFORMANCE_SETTING_KEY: Record<keyof CanvasPerformanceSettings, ProjectSettingKey> = {
  alwaysHideEdges: 'always_hide_edges',
  hideEdgesDuringInteraction: 'hide_edges_during_interaction',
  imagePreviewOnlyDuringInteraction: 'image_preview_only_during_interaction',
  lightweightImageNodeShell: 'lightweight_image_node_shell',
  lightweightNodeChrome: 'lightweight_node_chrome',
  previewPreservingInteractionLod: 'preview_preserving_interaction_lod',
  renderActiveEdgesOnly: 'render_active_edges_only',
  renderOnlyVisibleElements: 'render_only_visible_elements',
  snapToGrid: 'snap_to_grid',
  viewportPreviewLayer: 'viewport_preview_layer',
}

export const settingCategory = {
  toggle: (value: boolean): SettingValueCategory => (value ? 'on' : 'off'),
  aspectRatio: (value: string | null | undefined): SettingValueCategory | undefined => {
    if (value === null || value === undefined || value === '') return 'unset'
    if (value === '16:9' || value === '9:16') return value
    return undefined
  },
  videoResolution: (value: string | null | undefined): SettingValueCategory | undefined => {
    if (value === null || value === undefined || value === '') return 'unset'
    if (value === '720p' || value === '1080p' || value === '4k') return value
    return undefined
  },
  /** Style id: set or not (the id itself can name a user style). */
  style: (value: string | null | undefined): SettingValueCategory => (value ? 'explicit' : 'unset'),
  /** Content language: Auto (null / 'auto') or a concrete language. */
  contentLanguage: (value: string | null | undefined): SettingValueCategory =>
    !value || value === 'auto' ? 'auto' : 'explicit',
  /** Preferred video models: empty list = Auto. */
  modelList: (value: readonly string[] | null | undefined): SettingValueCategory =>
    value && value.length > 0 ? 'explicit' : 'auto',
  /** Preferred image model: 'auto' / empty = Auto. */
  model: (value: string | null | undefined): SettingValueCategory =>
    !value || value === 'auto' ? 'auto' : 'explicit',
}

/** `project_setting_changed`: call on the save-success path only. */
export function trackProjectSettingChanged({
  projectId,
  settingKey,
  next,
  previous,
}: {
  projectId: string
  settingKey: ProjectSettingKey
  next: SettingValueCategory | undefined
  previous?: SettingValueCategory
}): void {
  if (!projectId || next === undefined) return
  const props: ProjectSettingChangedProps = {
    project_id: projectId,
    setting_key: settingKey,
    new_value_category: next,
    previous_value_category: previous,
  }
  log({ event: 'project-setting-changed', ...props })
  track('project_setting_changed', props)
}

/**
 * `director_skills_picker_action(action=clear)` for a clear OUTSIDE the picker:
 * the selected-skill chip's x button, or deleting the chip with the keyboard.
 * No picker is open, so there is no picker_open_id (doc T2 #4).
 */
export function trackSkillChipCleared(
  projectId: string | null | undefined,
  skill: DirectorSkillSummary,
  source: SkillPickerSource,
): void {
  if (!projectId) return
  const skillRef = analyticsSkillRef(skill.ref)
  const skillVersionId = analyticsSkillVersionId(skill.versionId)
  const both = skillRef !== undefined && skillVersionId !== undefined
  const props: DirectorSkillsPickerActionProps = {
    project_id: projectId,
    action: 'clear',
    source,
    skill_ref: both ? skillRef : undefined,
    skill_version_id: both ? skillVersionId : undefined,
    skill_source: skillSourceOf(skill.source),
  }
  log({ event: 'skills-chip-cleared', ...props })
  track('director_skills_picker_action', props)
}

/**
 * `director_skills_picker_action(action=select)` for a skill picked from the
 * composer's "/" command menu. That menu is not the picker, so there is no
 * picker_open_id. `position` is the 1-based row in the menu.
 */
export function trackSlashSkillSelected(
  projectId: string | null | undefined,
  skill: DirectorSkillSummary,
  position: number | undefined,
): void {
  if (!projectId) return
  const skillRef = analyticsSkillRef(skill.ref)
  const skillVersionId = analyticsSkillVersionId(skill.versionId)
  const both = skillRef !== undefined && skillVersionId !== undefined
  const props: DirectorSkillsPickerActionProps = {
    project_id: projectId,
    action: 'select',
    entry_point: 'slash_command',
    source: 'slash_command',
    skill_ref: both ? skillRef : undefined,
    skill_version_id: both ? skillVersionId : undefined,
    skill_source: skillSourceOf(skill.source),
    selection_position_bucket: position === undefined ? undefined : countBucket(position),
  }
  log({ event: 'skills-slash-selected', ...props })
  track('director_skills_picker_action', props)
}

/** `chat_skill_library_action` for a click in the skill detail view or editor. */
export function trackSkillLibraryAction(
  projectId: string | null | undefined,
  action: SkillLibraryAction,
  skill: DirectorSkillSummary,
): void {
  const skillRef = analyticsSkillRef(skill.ref)
  const skillVersionId = analyticsSkillVersionId(skill.versionId)
  const both = skillRef !== undefined && skillVersionId !== undefined
  const props: ChatSkillLibraryActionProps = {
    project_id: projectId ?? undefined,
    action,
    skill_ref: both ? skillRef : undefined,
    skill_version_id: both ? skillVersionId : undefined,
    skill_source: skillSourceOf(skill.source),
  }
  log({ event: 'skill-library-action', ...props })
  track('chat_skill_library_action', props)
}
